import { RawJsonDict, TheApp, User } from "./base.model";

export class EmailVerifyItem {
    email: string;
    hasPassword: boolean = false;
    exists: boolean = false;

    static parse(data: any): EmailVerifyItem | null {
        if (!data) {
            return null;
        }

        const obj = new EmailVerifyItem();
        obj.email = data.email;
        obj.hasPassword = data.has_password ?? false;
        obj.exists = data.exists ?? false;

        return obj;
    }
}

export class EmailRequestCodeItem {
    email: string;
    exists: boolean = false;

    static parse(data: any): EmailRequestCodeItem | null {
        if (!data) {
            return null;
        }

        const obj = new EmailRequestCodeItem();
        obj.email = data.email;
        obj.exists = data.exists ?? false;

        return obj;
    }
}

export class EmailCodeVerifyItem {
    email: string;
    code: string;
    valid: boolean = false;

    static parse(data: any): EmailCodeVerifyItem | null {
        if (!data) {
            return null;
        }

        const obj = new EmailCodeVerifyItem();
        obj.email = data.email;
        obj.code = data.code;
        obj.valid = data.valid ?? false;

        return obj;
    }
}

export class ExternalUser extends RawJsonDict {
    id: number;
    app?: TheApp | null;
    user?: User | null;
    platform: string;
    uid: string;
    created?: Date;
    lastModified?: Date;

    constructor(data: any = null) {
        super(data);
    }

    static parse(data: any): ExternalUser | null {
        if (!data) {
            return null;
        }

        const obj = new ExternalUser(data);
        obj.id = data.id;
        obj.platform = data.platform;
        obj.uid = data.uid;

        if (data.app) {
            obj.app = TheApp.parse(data.app);
        }

        if (data.user) {
            obj.user = User.parse(data.user);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }
}

/** Model: Auth */
export class AuthCredential extends RawJsonDict {
    username?: string;
    accessToken: string;
    refreshToken: string;
    tokenType: string;
    scope: string;
    basicRole: string[] = [];
    level: number;
    expireDuration: number;
    tokenExpires: number;
    extra?: any;
    exchangeToken: string;

    constructor(data: any) {
        super(data);
    }

    static parse(data: any): AuthCredential | null {
        if (!data) {
            return null;
        }

        const obj = new AuthCredential(data);
        obj.accessToken = data.access_token;
        obj.refreshToken = data.refresh_token;
        obj.tokenType = data.token_type;
        obj.expireDuration = data.expires_in;
        obj.scope = data.scope;
        obj.basicRole = data.basic_roles ?? [];
        obj.level = data.level ?? 0;
        obj.extra = data.extra;
        obj.exchangeToken = data.exchange_token ?? '';

        if (data.expire_ts) {
            obj.tokenExpires = data.expire_ts;
        } else {
            obj.tokenExpires = (new Date().getTime() / 1000) + data.expires_in;
            obj.rawJson['expire_ts'] = obj.tokenExpires;
        }

        return obj;
    }

    isExpire(): boolean {
        try {
            return new Date() >= new Date(this.tokenExpires * 1000);
        } catch {
            return true;
        }
    }

    /** Seconds until the token expires; negative once expired. */
    secondsUntilExpire(): number {
        if (!this.tokenExpires) {
            return 0;
        }
        return this.tokenExpires - (new Date().getTime() / 1000);
    }
}

/** Model: Service Token */
export class ServiceToken extends RawJsonDict {
    id: number;
    uuid: string;
    valid: boolean;
    expireTs: number;
    accessToken: any;
    user?: User | null;
    app?: TheApp | null;
    meta: any;
    created?: Date;
    lastModified?: Date;

    constructor(data: any) {
        super(data);
    }

    static parse(data: any): ServiceToken | null {
        if (!data) {
            return null;
        }

        const obj = new ServiceToken(data);
        obj.id = data.id;
        obj.uuid = data.uuid;
        obj.valid = data.valid ?? false;
        obj.expireTs = data.expire_ts;
        obj.accessToken = data.access_token;
        obj.meta = data.meta ?? {};

        if (data.app) {
            obj.app = TheApp.parse(data.app);
        }

        if (data.user) {
            obj.user = User.parse(data.user);
        }

        if (data.created) {
            try {
                obj.created = new Date(data.created);
            } catch (error) {
                // Invalid date format
            }
        }

        if (data.last_modified) {
            try {
                obj.lastModified = new Date(data.last_modified);
            } catch (error) {
                // Invalid date format
            }
        }

        return obj;
    }
}